import React, { useEffect, useState } from "react";
import { ThumbtackAPI } from "../api";
import "./ThumbtackIntegration.css";

const EMPTY_FORM = {
  business_profile_name: "",
  receive_leads: true,
  receive_messages: true,
  receive_reviews: false,
};

function CopyField({ label, value, secret = false }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value || "");
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="tt-field">
      <div className="tt-label">{label}</div>
      <div className="tt-copy-row">
        <code data-testid={secret ? "one-time-password" : undefined}>
          {value}
        </code>
        <button type="button" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export default function ThumbtackIntegration() {
  const [connections, setConnections] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [oneTimeCredentials, setOneTimeCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  async function loadConnections() {
    const data = await ThumbtackAPI.list();
    setConnections(data);
  }

  useEffect(() => {
    let active = true;

    ThumbtackAPI.list()
      .then((data) => {
        if (active) setConnections(data);
      })
      .catch(() => {
        if (active) setMessage("Could not load Thumbtack connections.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      setOneTimeCredentials(null);
    };
  }, []);

  async function createConnection(event) {
    event.preventDefault();
    setWorking(true);
    setMessage("");

    try {
      const created = await ThumbtackAPI.create(form);
      setOneTimeCredentials(created);
      setForm(EMPTY_FORM);
      await loadConnections();
      setMessage("Connection created. Finish setup in Thumbtack now.");
    } catch {
      setMessage("Could not create the Thumbtack connection.");
    } finally {
      setWorking(false);
    }
  }

  async function rotate(connection) {
    if (!window.confirm(
      "Rotate these credentials? The existing Thumbtack webhook will stop until you update it."
    )) {
      return;
    }

    setWorking(true);
    setMessage("");

    try {
      const credentials = await ThumbtackAPI.rotate(connection.id);
      setOneTimeCredentials({
        ...connection,
        ...credentials,
      });
      setMessage("Credentials rotated. Update Thumbtack before leaving.");
      await loadConnections();
    } catch {
      setMessage("Could not rotate the credentials.");
    } finally {
      setWorking(false);
    }
  }

  async function disable(connection) {
    if (!window.confirm(
      "Disable this Thumbtack connection? New deliveries will be rejected."
    )) {
      return;
    }

    setWorking(true);
    setMessage("");

    try {
      await ThumbtackAPI.disable(connection.id);
      if (oneTimeCredentials?.id === connection.id) {
        setOneTimeCredentials(null);
      }
      await loadConnections();
      setMessage("Thumbtack connection disabled.");
    } catch {
      setMessage("Could not disable the connection.");
    } finally {
      setWorking(false);
    }
  }

  if (loading) {
    return <div className="tt-page">Loading Thumbtack integration…</div>;
  }

  return (
    <div className="tt-page">
      <div className="tt-heading">
        <div>
          <div className="tt-eyebrow">Tenant integration</div>
          <h1>Thumbtack</h1>
          <p>
            Send your Thumbtack Pro leads, customer messages, and reviews
            directly into this JobTelesis workspace.
          </p>
        </div>
        <div className="tt-actions">
          <a className="tt-secondary tt-link" href="/integrations/thumbtack/leads">
            Open lead inbox
          </a>
          <div className="tt-status-pill">
            {connections.some((item) => item.enabled)
              ? "Connection configured"
              : "Not connected"}
          </div>
        </div>
      </div>

      {message && <div className="tt-message">{message}</div>}

      {oneTimeCredentials && (
        <section className="tt-card tt-credential-card">
          <div className="tt-warning">Save the password now</div>
          <h2>Enter these values in Thumbtack</h2>
          <p>
            The password is displayed only during this session. If it is lost,
            rotate the credentials and update Thumbtack.
          </p>
          <CopyField
            label="Endpoint URL"
            value={oneTimeCredentials.endpoint_url}
          />
          <CopyField label="Authorization type" value="Basic" />
          <CopyField label="Username" value={oneTimeCredentials.username} />
          <CopyField
            label="One-time password"
            value={oneTimeCredentials.password}
            secret
          />
          <div className="tt-steps">
            <strong>In Thumbtack:</strong>
            <ol>
              <li>Open Apps → Webhooks → Create a webhook.</li>
              <li>Paste the endpoint URL.</li>
              <li>Select Basic and paste the username and password.</li>
              <li>Select the matching business profile and information types.</li>
              <li>Leave Status enabled and create the webhook.</li>
            </ol>
          </div>
          <button
            type="button"
            className="tt-secondary"
            onClick={() => setOneTimeCredentials(null)}
          >
            I saved these values
          </button>
        </section>
      )}

      <section className="tt-card">
        <h2>Create a connection</h2>
        <p>
          Create one connection for each Thumbtack business profile that should
          deliver information to this workspace.
        </p>

        <form onSubmit={createConnection} className="tt-form">
          <label>
            <span>Business profile name</span>
            <input
              value={form.business_profile_name}
              onChange={(event) => setForm({
                ...form,
                business_profile_name: event.target.value,
              })}
              placeholder="Example: Dfasio Construction"
              maxLength={255}
            />
          </label>

          <fieldset>
            <legend>Information to receive</legend>
            {[
              ["receive_leads", "Lead details", "New leads, job details, and status updates"],
              ["receive_messages", "Messages", "New customer messages"],
              ["receive_reviews", "Reviews", "New customer reviews"],
            ].map(([key, title, description]) => (
              <label className="tt-check" key={key}>
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(event) => setForm({
                    ...form,
                    [key]: event.target.checked,
                  })}
                />
                <span>
                  <strong>{title}</strong>
                  <small>{description}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <button type="submit" className="tt-primary" disabled={working}>
            {working ? "Creating…" : "Generate Thumbtack connection"}
          </button>
        </form>
      </section>

      <section className="tt-card">
        <h2>Connections</h2>
        {connections.length === 0 ? (
          <p>No Thumbtack connection has been created for this workspace.</p>
        ) : (
          <div className="tt-connections">
            {connections.map((connection) => (
              <article className="tt-connection" key={connection.id}>
                <div>
                  <h3>
                    {connection.business_profile_name || "Thumbtack business"}
                  </h3>
                  <div className={connection.enabled ? "tt-on" : "tt-off"}>
                    {connection.enabled ? "Enabled" : "Disabled"}
                  </div>
                </div>
                <dl>
                  <dt>Business ID</dt>
                  <dd>{connection.business_profile_id || "Waiting for first delivery"}</dd>
                  <dt>Last delivery</dt>
                  <dd>
                    {connection.last_received_at
                      ? new Date(connection.last_received_at).toLocaleString()
                      : "No deliveries yet"}
                  </dd>
                  <dt>Receives</dt>
                  <dd>
                    {[
                      connection.receive_leads && "Leads",
                      connection.receive_messages && "Messages",
                      connection.receive_reviews && "Reviews",
                    ].filter(Boolean).join(", ") || "Nothing selected"}
                  </dd>
                </dl>
                {connection.enabled && (
                  <div className="tt-actions">
                    <button
                      type="button"
                      className="tt-secondary"
                      onClick={() => rotate(connection)}
                      disabled={working}
                    >
                      Rotate credentials
                    </button>
                    <button
                      type="button"
                      className="tt-danger"
                      onClick={() => disable(connection)}
                      disabled={working}
                    >
                      Disable
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
